import React, { useState } from 'react';
import { useStripeConnect } from '../../hooks/useStripeConnect';
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js';
import styled from 'styled-components';
import Typography from '../Typography';
import Button from '../Button/Button';
import BillingProfileForm from '../BillingProfile/BillingProfileForm';
import { useAuth } from '../../context/AuthContext';

function StripeOnboardingPage({ onClose }) {
  const { user, refreshUserData, refreshStripeStatus } = useAuth();
  const [connectedAccountId, setConnectedAccountId] = useState(null);
  const [error, setError] = useState(false);
  const [onboardingExited, setOnboardingExited] = useState(false);
  const [accountCreatePending, setAccountCreatePending] = useState(false);

  // Fiscal profile is a prerequisite for issuing the monthly recapitulative
  // invoice — the backend ALSO blocks paid checkout with 422 if it's missing,
  // but we gate the Stripe Connect onboarding UI here so the organizer never
  // hits that error in the first place.
  const [step, setStep] = useState(user?.billingProfileCompleted ? 'stripe' : 'billing');

  // Este hook inicializa Connect.js con el ID de la cuenta
  const stripeConnectInstance = useStripeConnect(connectedAccountId);

  const handleSignUp = async () => {
    setAccountCreatePending(true);
    setError(false);

    try {
      // const resp = await fetch('/stripe/account', { method: 'POST' });
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/account`, { method: 'POST' })
      const json = await resp.json();

      if (json.account) {
        setConnectedAccountId(json.account);  // 'acc_123...'
      } else if (json.error) {
        setError(true);
      }
    } catch (error) {
      setError(true);
      console.error('Error al crear cuenta:', error);
    } finally {
      setAccountCreatePending(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalContent>
        {step === 'billing' && (
          <>
            <Button onClick={onClose}>X</Button>
            <Typography as="h4" $variant="title-5-semibold">Antes de empezar: datos de facturación</Typography>
            <Typography as="p">
              Para emitirte la factura mensual de la comisión de Motorik necesitamos tu razón social y NIF/VAT ID.
              Solo te lo pediremos una vez.
            </Typography>
            <BillingProfileForm
              initialValues={{
                legalName: user?.legalName,
                taxId: user?.taxId,
                billingCountry: user?.billingCountry,
                vatTreatment: user?.vatTreatment,
                billingAddress: user?.billingAddress
              }}
              onSuccess={() => setStep('stripe')}
              submitLabel="Continuar con Stripe"
            />
          </>
        )}

        {step === 'stripe' && !connectedAccountId &&
          <>
            <Button onClick={onClose}>X</Button>

            <IntegrationIcon src="/icons/motorik-stripe-connect.svg" alt="Stripe Integration" />
            <Typography as="h4" $variant="title-5-semibold">Comienza a recibir pagos en 5 minutos.</Typography>
            <Typography as="p">Utilizamos Stripe como nuestro procesador de pagos. Conéctate o configura una cuenta de Stripe para comenzar a aceptar pagos</Typography>
          </>
        }

        {/* Botón para crear cuenta si no la tienes */}
        {step === 'stripe' && !connectedAccountId && !accountCreatePending && (
          <Button onClick={handleSignUp}>
            Conectar con Stripe
          </Button>
        )}

        {accountCreatePending &&
          <Typography as="p">Creando una cuenta conectada...</Typography>
        }

        {error && <Typography as="p">Ocurrió un error</Typography>}

        {/* Muestra Connect.js Onboarding cuando ya tenemos connectedAccountId */}

        {connectedAccountId && !stripeConnectInstance && (
          <h2>Add info to start accepting money</h2>
        )}

        {stripeConnectInstance && (
          <>
            <Typography as="h4" $variant="title-5-semibold">Crea tu cuenta con Stripe</Typography>
            <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
              <ConnectAccountOnboarding
                onExit={async () => {
                  console.log('Onboarding finalizado.');
                  setOnboardingExited(true);
                  
                  // Actualizar datos del usuario y estado de Stripe
                  if (user) {
                    await refreshUserData();
                    await refreshStripeStatus(user.id);
                  }
                  
                  // Cerrar el modal después de un breve delay
                  setTimeout(() => {
                    onClose();
                  }, 1000);
                }}
              />
            </ConnectComponentsProvider>
          </>
        )}

        {onboardingExited && (
          <p>The Account Onboarding component has exited.</p>
        )}

        {/* Info dev para dev */}
        {connectedAccountId && (
          <div>
            <p>Connected account ID: {connectedAccountId}</p>
          </div>
        )}
      </ModalContent>
    </ModalOverlay>
  );
}

export default StripeOnboardingPage;


const ModalOverlay = styled.div`
  align-items: center;
  backdrop-filter: blur(8px);
  background-color: rgb(0 0 0 / 50%);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
`;

const ModalContent = styled.div`
  background-color: #fff;
  border-radius: 8px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
  width: 500px;
`;

const IntegrationIcon = styled.img`
  max-height: 68px;
  width: 100%;
`;
